import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import LoggerService from "@bfx/logger";
import { MenuItem } from "@bfx/primeng/api";
import { SplitButton } from "@bfx/primeng/splitbutton";

import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";
const logger = LoggerService.for(__dirname);

const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxDropdownButtonComponent),
  multi: true,
};

type EmphasisType = "subtle" | "moderate" | "bold";
type IntentType = "primary" | "secondary";

/**
 * Simple control that shows all input form labels in a dropdown control view. Current selected value
 * stored and styled.
 *
 * Changes managed by Forms model
 */
@Component({
  selector: "bfx-dropdown-button",
  templateUrl: "./html/bfx-dropdown-button.html",
  providers: [VALUE_ACCESSOR],
  standalone: false,
})
export class BfxDropdownButtonComponent implements OnChanges, ControlValueAccessor {
  public currentValue: MenuItem = null;

  /** ControlValueAccessor callback */
  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;

  /** ControlValueAccessor callback */
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  private disabled: boolean = false;

  @Input()
  public labels: Array<string> = [];

  @Input()
  public menuItems: Array<MenuItem> = [];

  @Input()
  public emphasis: EmphasisType = "subtle";

  @Input()
  public type: IntentType = "primary";

  public splitMenuItems: MenuItem[] = [];

  public buttonClass: string;

  @ViewChild("splitOptions")
  private splitOptions: SplitButton;

  constructor(private _element: ElementRef) {
    this.initStyles();
  }

  private initStyles() {
    this.buttonClass = `bfx-button-sm bfx-button-${this.type}-${this.emphasis}`;
  }

  private initMenuItems() {
    this.splitMenuItems = [];
    const numLabels = this.labels?.length ?? 0;
    const numMenuItems = this.menuItems?.length ?? 0;

    if (numLabels > 0 && numMenuItems > 0) {
      logger.out.warn(
        `Noth labels and menu items defned for drop down - menu items will take precedence`,
      );
    }

    if (numMenuItems > 0) {
      this.createSplitMenuItemsFromMenuItems();
    } else if (numLabels > 0) {
      this.createSplitMenuItemsFromLabels();
    }

    if (this.currentValue == null && this.splitMenuItems.length) {
      // Set the value to first in the list if no current value currently defined
      this.setValueInternal(this.splitMenuItems[0], false);
    }
  }

  private createSplitMenuItemsFromLabels() {
    for (const label of this.labels) {
      this.splitMenuItems.push({ label });
    }
    this.splitMenuItems = this.labels.map((value) => {
      const retItem: MenuItem = {
        label: value,
      };
      retItem.command = () => {
        this.setValueInternal(retItem);
      };
      return retItem;
    });
  }
  private createSplitMenuItemsFromMenuItems() {
    for (const menuItem of this.menuItems) {
      const newItem: MenuItem = { ...menuItem };
      newItem.command = () => {
        this.setValueInternal(menuItem);
        if (typeof menuItem.command === "function") {
          menuItem.command();
        }
      };
      this.splitMenuItems.push(newItem);
    }
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.initStyles();
    this.initMenuItems();
  }

  /**
   * Initialize value from blotter configuration in case this component is attached to
   * blotter filter bar.
   */
  ngOnInit() {}

  public setValueInternal(value: MenuItem, notify: boolean = true) {
    this.currentValue = value;
    if (notify) {
      this.onModelChange(this.currentValue.label);
    }
  }

  writeValue(value: string): void {
    const newValue = this.splitMenuItems.find((menuItem) => menuItem.label === value);
    if (newValue) {
      this.currentValue = newValue;
    }
  }

  registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  showMenu(): void {
    if (this.splitOptions) {
      this.splitOptions.dropdownClick = true;
      this.splitOptions.overlayVisible = !this.splitOptions.overlayVisible;
    }
  }
}
